import { nanoid } from 'nanoid';
import { supabase, authReady } from '../supabase';
import { getAnonUserId } from '../../utils/identityUtils';
import { CONNECTION_METADATA_PREFIX } from '../storage/userDataStorage';
import { logEvent, logFunnelSignupConfirmed } from '../analytics/eventLogger';
import { SOURCE_BRANCH } from '../../utils/deployInfo';
import { normalizePhone } from '../../utils/phoneNumber';

/**
 * Pending Profile Service
 *
 * Handles Anon→Profile conversion via server-side pending records.
 * The browser creates a pending record + syncs localStorage data to Supabase,
 * then the PWA completes conversion after OTP verification.
 *
 * See docs/features/pending-profile-conversion.md for full design.
 */

/**
 * The answer when the number typed into Join already belongs to a Full
 * account (Mark's copy; registered in ui-design-system.md). Nothing is put
 * into pending; the server links the number to this session's own Simple
 * profile out of sight, so their login brings its connections along
 * (f/simple-merge, migration 039).
 */
export const ACCOUNT_EXISTS_MESSAGE =
  'You already have an account with this number. Open the Royale app to log in.';

/** upsert_pending_profile v4's sentinel for a Full number (039). */
const ACCOUNT_EXISTS_SENTINEL = 'account_exists';

/**
 * upsert_pending_profile v5's sentinel (042, Decision 3): this number is
 * already waiting from another session (another phone, or before a data
 * clear). That Join stands — nothing is staged or synced from here — and the
 * person carries on as on success: the text-code login converts the waiting
 * entry. Chats on this phone don't carry into the account.
 */
const PENDING_ELSEWHERE_SENTINEL = 'pending_elsewhere';

// ─── Sync payload builder ───────────────────────────────────────────────────

/**
 * Build the sync payload for a single connection.
 * Reads from Anon localStorage keys and maps to connection_data column format.
 *
 * Anon key → connection_data column → Recovery writes to Profile key:
 *   connection_metadata_${id}        → metadata          → connection_metadata_${id}
 *   received_profile_data_${id}      → received_contact   → received_anon_data_${id}
 *   anon_notes_${id}                 → notes             → profile_notes_${id}
 *   anon_user_data_${id}.sharedFields → sent_shared_fields → sent_shared_fields_${id}
 *   !!anon_user_data_${id}           → contact_shared     → contact_shared_${id}
 *   !!anon_user_data_${id}           → contact_card_sent  → contact_card_sent_${id}
 */
const getJSON = (key) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch {
    return null;
  }
};

export const buildSyncPayload = (chatRoomId) => {
  const anonUserData = getJSON(`anon_user_data_${chatRoomId}`);
  return {
    chat_room_id: chatRoomId,
    data: {
      metadata: getJSON(`connection_metadata_${chatRoomId}`),
      received_contact: getJSON(`received_profile_data_${chatRoomId}`),
      notes: localStorage.getItem(`anon_notes_${chatRoomId}`) || null,
      sent_shared_fields: anonUserData?.sharedFields || null,
      contact_shared: !!anonUserData,
      contact_card_sent: !!anonUserData,
    },
  };
};

/**
 * Build sync payloads for ALL connections in localStorage.
 * Enumerates connections by scanning for connection_metadata_* keys.
 */
export const buildAllSyncPayloads = () => {
  const payloads = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CONNECTION_METADATA_PREFIX)) {
      const chatRoomId = key.replace(CONNECTION_METADATA_PREFIX, '');
      payloads.push(buildSyncPayload(chatRoomId));
    }
  }
  return payloads;
};

// ─── Phone extraction from chat ─────────────────────────────────────────────

/**
 * Extract the Anon's phone number from contact_share messages in localStorage.
 * Looks for phone/whatsapp entries in the Anon's shared contacts.
 * Returns the first phone number found, or null.
 */
export const extractAnonPhone = (chatRoomId) => {
  const messages = getJSON(`chat_messages_${chatRoomId}`);
  if (!messages || !Array.isArray(messages)) return null;

  const anonUserId = getAnonUserId();

  // Find contact_share messages sent by this Anon
  for (const msg of messages) {
    if (msg.type === 'contact_share' && msg.senderId === anonUserId && msg.content?.contacts) {
      const phoneEntry = msg.content.contacts.find(
        (c) => c.type === 'phone' || c.type === 'whatsapp'
      );
      if (phoneEntry?.value) return phoneEntry.value;
    }
  }
  return null;
};

/**
 * Extract phone from ANY connection's chat messages.
 * Scans all connections to find a phone the Anon shared.
 */
export const extractAnonPhoneFromAnyConnection = () => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CONNECTION_METADATA_PREFIX)) {
      const chatRoomId = key.replace(CONNECTION_METADATA_PREFIX, '');
      const phone = extractAnonPhone(chatRoomId);
      if (phone) return phone;
    }
  }
  return null;
};

// ─── RPC wrappers ───────────────────────────────────────────────────────────

/**
 * Create a pending profile record and sync all connection data.
 * Called when the Anon confirms their phone number.
 *
 * Both RPCs must succeed before showing install instructions.
 * Read-only on localStorage — the Anon keeps browsing normally.
 *
 * @param {string} phone - Phone number (will be normalized to E.164)
 * @returns {{ error: string|null, profileUserId: string|null }}
 */
export const createPendingProfile = async (phone) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) {
    return { error: 'Invalid phone number', profileUserId: null };
  }

  // The server records the creating session as the proof conversion uses
  // (migration 037) — never call it before sign-in has resolved.
  await authReady;
  const anonUserId = getAnonUserId();
  const profileUserId = nanoid(6);

  // Get the most recent chatRoomId (for the pending record)
  let chatRoomId = null;
  let latestTime = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CONNECTION_METADATA_PREFIX)) {
      const id = key.replace(CONNECTION_METADATA_PREFIX, '');
      const meta = getJSON(key);
      const time = meta?.lastUpdated ? new Date(meta.lastUpdated).getTime() : 0;
      if (time > latestTime) {
        latestTime = time;
        chatRoomId = id;
      }
    }
  }

  // Step 1: Create pending record — RPC returns the actual profile_user_id
  // (may differ from generated ID on re-confirmation, since the original is preserved)
  const { data: actualProfileUserId, error: upsertError } = await supabase.rpc(
    'upsert_pending_profile',
    {
      p_phone: normalizedPhone,
      p_profile_user_id: profileUserId,
      p_anon_user_id: anonUserId,
      p_chat_room_id: chatRoomId,
    }
  );

  if (upsertError) {
    console.error('[PENDING_PROFILE] upsert failed:', upsertError.message);
    return { error: upsertError.message, profileUserId: null };
  }

  // A Full account already owns this number: nothing was staged — no data
  // sync, no signup funnel event (039)
  if (actualProfileUserId === ACCOUNT_EXISTS_SENTINEL) {
    console.log('[PENDING_PROFILE] Number belongs to a Full account — nothing staged');
    logEvent('signup_account_exists', { chatRoomId });
    return { error: ACCOUNT_EXISTS_MESSAGE, profileUserId: null };
  }

  if (actualProfileUserId === PENDING_ELSEWHERE_SENTINEL) {
    console.log('[PENDING_PROFILE] Number already waiting from another session — that Join stands');
    logEvent('signup_pending_elsewhere', { chatRoomId });
    return { error: null, profileUserId: null };
  }

  // Use the actual ID from the record (not the generated one) for sync
  const effectiveProfileUserId = actualProfileUserId || profileUserId;

  // Step 2: Sync all connection data
  const payloads = buildAllSyncPayloads();
  if (payloads.length > 0) {
    const { error: syncError } = await supabase.rpc('sync_pending_connection_data', {
      p_profile_user_id: effectiveProfileUserId,
      p_connections: payloads,
      p_source_branch: SOURCE_BRANCH,
    });

    if (syncError) {
      console.error('[PENDING_PROFILE] sync failed:', syncError.message);
      return { error: syncError.message, profileUserId: null };
    }
  }

  console.log(
    `[PENDING_PROFILE] Created pending profile for ${normalizedPhone} ` +
      `(${effectiveProfileUserId}), synced ${payloads.length} connections`
  );

  // Log analytics — deduped per Anon via localStorage guard in logFunnelSignupConfirmed
  logFunnelSignupConfirmed('anon_conversion', normalizedPhone, !!anonUserId, chatRoomId);

  return { error: null, profileUserId: effectiveProfileUserId };
};
